import React, { useEffect, useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Stepper from '@mui/material/Stepper';
import MobileStepper from '@mui/material/MobileStepper';
import Step from '@mui/material/Step';
import { Button } from 'react-bootstrap';
import Paper from '@mui/material/Paper';
import { css, StepConnector, stepConnectorClasses, StepLabel, stepLabelClasses, styled } from '@mui/material';
import PropTypes from 'prop-types';
import { isNumber } from 'lodash';
import { Check } from '@mui/icons-material';

const StepperCustomLabel = styled(StepLabel)(() => ({
  [`& .${stepLabelClasses.alternativeLabel}`]: {
    marginTop: '4px !important',
    fontWeight: '600',
  },
}));

const StepperStyleConnector = styled(StepConnector)(() => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: {
    top: 13,
    left: 'calc(-50% + 28px)',
    right: 'calc(50% + 28px)',
  },
  [`&.${stepConnectorClasses.active}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      borderColor: '#784af4',
    },
  },
  [`&.${stepConnectorClasses.completed}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      borderColor: '#784af4',
    },
  },
  [`& .${stepConnectorClasses.line}`]: {
    borderColor: '#eaeaf0',
    borderTopWidth: 3,
    borderRadius: 1,
  },
}));

const CustomStepContent = styled('div')`
  width: 100%;
  display: flex;
  flex-direction: column;
  transition: min-height 0.3s ease-in-out;
`;

export const StepButtonWrapper = styled('div')`
  display: flex;
  justify-content: space-between;
  width: 100%;

  @media (max-width: 400px) {
    flex-direction: column;
    gap: 7px;
    align-items: center;
    display: flex;
    justify-content: center;
    flex-direction: column-reverse;
  }

  // New wrapper for checkout-next-button
  .next-button-wrapper {
    display: flex;
    justify-content: flex-end;
    width: 100%;
    @media (max-width: 168px) {
      justify-content: center;
    }
  }

  .checkout-prev-button {
    width: 118px;
    height: 52px;
    border-radius: 6px;
    line-height: 19.84px;
    background: transparent !important;
    font-weight: 500;

    &:focus {
      box-shadow: 0 0 0 0.2rem rgba(120, 74, 244, 0.25) !important;
    }
  }

  .checkout-next-button {
    border: none;
    border-radius: 6px;
    font-weight: 700;
    line-height: 19.84px;
    padding: 16px 48px;
    min-width: 150px;
    transition: all 0.2s ease;

    @media (max-width: 768px) {
      margin-right: 0px;
    }

    &:hover {
      transform: translateY(-1px);
    }

    &:active {
      border: none;
      transform: translateY(0);
    }

    &:focus {
      box-shadow: 0 4px 14px rgba(120, 74, 244, 0.4), 0 0 0 0.2rem rgba(120, 74, 244, 0.25);
    }

    &:disabled {
      background: #9e9e9e !important;
      box-shadow: none;
      cursor: not-allowed;
      transform: none;
    }
  }
`;

const ButtonsContainer = styled('div')`
  display: flex;
  width: 100%;
  justify-content: space-between;
  ${(props) =>
    props.singleMode &&
    css`
      justify-content: flex-end;
      align-items: flex-end;
    `}

  @media (max-width: 168px) {
    flex-direction: column-reverse;
    align-items: stretch;

    ${(props) =>
      props.singleMode &&
      css`
        align-items: flex-end;
      `}

    & > * {
      margin-bottom: 10px;
    }

    & > *:first-child {
      margin-bottom: 0;
    }
  }
`;
const StepIcon = styled('div')(({ ownerState }) => ({
  color: 'white',
  display: 'flex',
  width: 22,
  height: 22,
  '& .QontoStepIcon-completedIcon': {
    zIndex: 1,
    fontSize: 18,
    backgroundColor: '#000000',
    borderRadius: '50%',
  },
  borderRadius: '50%',
  border: '3px solid #000000',
  alignItems: 'center',
  ...(ownerState.active && {
    backgroundColor: 'inherit',
  }),
  ...(ownerState.completed && {
    backgroundColor: 'inherit',
    fontSize: 18,
  }),
}));

function StepIconProvider(props) {
  const { active, completed, className } = props;

  return (
    <StepIcon ownerState={{ active, completed }} className={`${className}`}>
      {completed ? <Check className="QontoStepIcon-completedIcon" /> : <div className="QontoStepIcon-circle" />}
    </StepIcon>
  );
}

StepIconProvider.propTypes = {
  active: PropTypes.bool,
  className: PropTypes.string,
  completed: PropTypes.bool,
};

export const CheckoutProcessStepper = ({
  cart,
  event,
  steps,
  isCompleted = false,
  settings,
  onResizeComponent,
  setCurrentStep,
  activeStep,
  tickets,
  isTimeBasedEvents,
  occurrencesDate,
  isCalendar = true,
}) => {
  const containerRef = useRef(null);

  const scrollToCheckoutElement = () => {
    const eventElement = document.querySelector(`.tickets-${event.get('id')}`);
    if (eventElement) {
      eventElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const isNextButtonDisable = useMemo(() => {
    if (!event) return true;
    if (isTimeBasedEvents) {
      if (activeStep === 0 && isCalendar && !!occurrencesDate) return false;

      return !tickets.size;
    }

    return !Object.keys(cart).length;
  }, [activeStep, tickets, event, cart, occurrencesDate]);

  const handleNext = (step, index) => () => {
    if (step && step.validate()) {
      const shouldSkip = steps[index + 1] && steps[index + 1].shouldSkip();
      setCurrentStep(activeStep + (!shouldSkip ? 1 : 2));
      scrollToCheckoutElement();
      onResizeComponent && onResizeComponent(true);
    }
  };

  const handlePreviousStep = () => () => {
    if (!isNumber(activeStep)) return;

    if (activeStep <= 0) return;

    setCurrentStep(activeStep - 1);
    scrollToCheckoutElement();
    onResizeComponent && onResizeComponent();
  };

  useEffect(() => {
    if (containerRef.current) {
      const currentStepContent = document.querySelector(`#step-${activeStep} .checkout-step-label`);
      if (currentStepContent) {
        containerRef.current.style.minHeight = `${currentStepContent.offsetHeight}px`;
      }
    }
  }, [activeStep, steps]);

  return (
    <Box>
      <React.Fragment>
        <Stepper
          sx={{
            marginBottom: '32px',
          }}
          alternativeLabel
          ref={containerRef}
          activeStep={activeStep}
          orientation="horizontal"
          id="desktop-stepper"
          connector={<StepperStyleConnector />}
        >
          {steps.map((step, index) => (
            <Step key={step.label} id={`step-${index}`}>
              <StepperCustomLabel StepIconComponent={StepIconProvider} className="checkout-step-label ">
                {step.label}
              </StepperCustomLabel>
            </Step>
          ))}
        </Stepper>
        <CustomStepContent>
          {steps[activeStep]?.description}
          <ButtonsContainer singleMode={activeStep < 1}>
            {activeStep >= 1 && !(activeStep === steps.length - 1) && (
              <StepButtonWrapper>
                <Button variant="outline-dark" onClick={handlePreviousStep()} className="checkout-prev-button checkout-back-button">
                  {settings.get('checkoutBackButtonText')}
                </Button>
              </StepButtonWrapper>
            )}
            {activeStep < steps.length - 1 && (
              <StepButtonWrapper>
                <div className="next-button-wrapper">
                  <Button disabled={isNextButtonDisable} onClick={handleNext(steps[activeStep], activeStep)} className="checkout-next-button">
                    {settings.get('checkoutNextButtonText')}
                  </Button>
                </div>
              </StepButtonWrapper>
            )}
          </ButtonsContainer>
        </CustomStepContent>
      </React.Fragment>
      {isCompleted && (
        <Paper square elevation={0} sx={{ p: 3 }}>
          {settings.get('checkoutSuccessText')}
        </Paper>
      )}
    </Box>
  );
};

export function TextMobileStepper({ steps, onResizeComponent, settings }) {
  const [activeStep, setActiveStep] = useState(0);
  const maxSteps = steps.length;

  const handleNext = () => {
    const step = steps[activeStep];
    if (step && step.validate()) {
      const shouldSkip = steps[activeStep + 1] && steps[activeStep + 1].shouldSkip();
      setActiveStep((prevActiveStep) => prevActiveStep + (!shouldSkip ? 1 : 2));
      onResizeComponent && onResizeComponent(true);
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  return (
    <Box sx={{ flexGrow: 1, p: 0, marginBottom: 32 }}>
      <Box sx={{ width: '100%', p: 0 }} id="mobile-stepper">
        {steps[activeStep].description}
      </Box>
      <MobileStepper
        steps={maxSteps}
        position="static"
        activeStep={activeStep}
        nextButton={
          <Button size="small" onClick={handleNext} disabled={activeStep === maxSteps - 1} className="checkout-next-button">
            {settings.get('checkoutNextButtonText')}
          </Button>
        }
        backButton={
          <Button size="small" onClick={handleBack} disabled={activeStep === 0} className="checkout-next-button">
            {settings.get('checkoutBackButtonText')}
          </Button>
        }
      />
    </Box>
  );
}
